/**
 * 🧭 REDIRECT HELPER - Guides users to new unified interfaces
 * Shows helpful messages when redirecting from old pages to new consolidated ones
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  ArrowForward,
  Info,
  CheckCircle,
  Dashboard,
  People,
  VerifiedUser,
} from '@mui/icons-material';

interface RedirectHelperProps {
  from: string;
  to: string;
  title: string;
  description: string;
  benefits?: string[];
  autoRedirectDelay?: number; // seconds, default 5
}

export const RedirectHelper: React.FC<RedirectHelperProps> = ({
  from,
  to,
  title,
  description,
  benefits = [],
  autoRedirectDelay = 5,
}) => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = React.useState(autoRedirectDelay);
  const [redirecting, setRedirecting] = React.useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setRedirecting(true);
          navigate(to, { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, to]);

  const handleRedirectNow = () => {
    setRedirecting(true);
    navigate(to, { replace: true });
  };

  const handleStayHere = () => {
    setCountdown(-1); // Stop countdown
  };

  if (redirecting) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 1 }}>
          Redirecting to {title}...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Info sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Page Moved!
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This functionality has been consolidated for a better experience
              </Typography>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>{from}</strong> functionality is now part of <strong>{title}</strong>
            </Typography>
          </Alert>

          <Typography variant="body1" sx={{ mb: 3 }}>
            {description}
          </Typography>

          {benefits.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                What's improved:
              </Typography>
              <Stack spacing={1}>
                {benefits.map((benefit, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle sx={{ color: 'success.main', fontSize: 16 }} />
                    <Typography variant="body2">{benefit}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}

          <Box sx={{ 
            p: 2, 
            backgroundColor: 'action.hover', 
            borderRadius: 1, 
            mb: 3,
            textAlign: 'center'
          }}>
            {countdown > 0 ? (
              <Typography variant="body2" color="text.secondary">
                Redirecting automatically in <Chip label={countdown} size="small" color="primary" /> seconds
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Auto-redirect stopped
              </Typography>
            )}
          </Box>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              startIcon={<ArrowForward />}
              onClick={handleRedirectNow}
              size="large"
            >
              Go to {title}
            </Button>
            {countdown > 0 && (
              <Button
                variant="outlined"
                onClick={handleStayHere}
                size="large"
              >
                Stay on Original Page
              </Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

// Pre-configured redirect helpers for common scenarios
export const UserManagementRedirect: React.FC = () => (
  <RedirectHelper
    from="User Management"
    to="/users/hub"
    title="User Hub"
    description="User management has been upgraded to a unified hub that handles all user types, roles, and verification stages in one place."
    benefits={[
      "See all users (renters, hosts, drivers) in one interface",
      "Track multi-role users who are both hosts and drivers",
      "Unified approval pipeline for all verification types",
      "Better search and filtering capabilities",
      "Integrated onboarding tracking and follow-up"
    ]}
  />
);

export const DriverManagementRedirect: React.FC = () => (
  <RedirectHelper
    from="Driver Management"
    to="/users/hub"
    title="User Hub"
    description="Driver management is now integrated into the User Hub, providing a complete view of drivers within the broader user ecosystem."
    benefits={[
      "See drivers alongside their other roles (many are also hosts)",
      "Integrated driver verification and approval workflow",
      "Better tracking of driver onboarding progress",
      "Unified view of all driver-related actions and follow-ups",
      "Enhanced search to find drivers by multiple criteria"
    ]}
  />
);

export const LegacyApprovalRedirect: React.FC<{ targetTab?: number }> = ({ targetTab = 1 }) => (
  <RedirectHelper
    from="Legacy Approval Page"
    to={`/users/hub?tab=${targetTab}`}
    title="Unified Approval Pipeline"
    description="The approval process has been streamlined into a comprehensive pipeline that handles both verification and business approval stages."
    benefits={[
      "Complete view of the entire approval workflow",
      "See items flow from verification to business approval",
      "Unified stats and metrics across both stages",
      "Better tracking of approval bottlenecks",
      "Single interface for all approval types (users, drivers, vehicles, hosts)"
    ]}
  />
);